import React from "react";
import ForceGraph from "react-force-graph-2d";
import useDataTypeImgs from "../hooks/useDataTypeImgs";
import useForceTweak from "../hooks/useForceTweak";
import { TCanvasLink } from "../types/TCanvasLink";
import { TCanvasNode } from "../types/TCanvasNode";
import { TCanvasOptions } from "../types/TCanvasOptions";
import drawLinks from "../utils/drawLinks";
import drawNodes from "../utils/drawNodes";
import getNodeSize from "../utils/getNodeSize";
import hoverAreaLink from "../utils/hoverAreaLink";
import useItemHover from "../hooks/useItemHover";
import { TRenderActionType, TRenderPayload } from "../types/TRenderEvent";
import useContext from "../../hooks/useContext";
import { TransitionContext } from "../context/TransitionProvider";
import BackgroundParticles from "./BackgroundParticles";

interface IProps {
  width?: number
  height?: number
  nodes: TCanvasNode[]
  links: TCanvasLink[]
  canvasOptionRef: React.MutableRefObject<TCanvasOptions | null>
  physicsParams?: any
  shouldReheatSimulation?: boolean
  highlightedNodeIds?: number[]
  updateRenderEvent: (
    event?: MouseEvent,
    action_type?: TRenderActionType,
    payload?: TRenderPayload,
  ) => void
}

type TDimension = {
  width: number | null
  height: number | null
}

const Canvas = React.memo(( { nodes, links, updateRenderEvent, canvasOptionRef, physicsParams, shouldReheatSimulation, highlightedNodeIds }: IProps) => {

      const [dim, setDim] = React.useState<TDimension>({ width: null, height: null })
      const parentRef = React.useRef<HTMLDivElement>(null)
      const { shouldGreyOutNodes, updateNodeHover, updateLinkHover, handleNodePinning, pinnedNode } = useItemHover();
      const { imgs } = useDataTypeImgs()

      const { fgRef } = useForceTweak(canvasOptionRef.current, physicsParams)
      const { transition } = useContext(TransitionContext)
      
      // Reheat simulation when flag is set (after classification completes)
      React.useEffect(() => {
        if (shouldReheatSimulation && fgRef.current) {
          // Get the d3 simulation
          const simulation = fgRef.current.d3Force();
          
          if (simulation) {
            // Unfix all nodes (remove fx, fy to allow dragging)
            nodes.forEach(node => {
              if (node.fx !== undefined) delete node.fx;
              if (node.fy !== undefined) delete node.fy;
            });
            
            // Set a strong alpha and restart simulation
            simulation.alpha(0.3);
            simulation.restart();
          }
          
          // Also call reheat if available
          if (fgRef.current.d3ReheatSimulation) {
            fgRef.current.d3ReheatSimulation();
          }
        }
      }, [shouldReheatSimulation, fgRef, nodes]);

      React.useEffect(() => {
        if (parentRef.current) {
          setDim({
            width: parentRef.current.clientWidth,
            height: parentRef.current.clientHeight,
          })
        }
        window.onresize = function() {
          if (parentRef.current) {
            setDim({
              width: parentRef.current.clientWidth,
              height: parentRef.current.clientHeight,
            })
          }
        }
      }, [])


      return (
        <div ref={parentRef} className="w-full h-full canvas-cover">
          {/* Show background particles only when no actual data nodes are present */}
          {nodes.length === 0 && <BackgroundParticles />}
                    
          <ForceGraph
            width={dim.width != null ? dim.width : undefined}
            height={dim.height != null ? dim.height : undefined}
            ref={fgRef}
            autoPauseRedraw={false}
            d3AlphaMin={0}
            d3VelocityDecay={0.2}
            d3AlphaDecay={0.015}
            graphData={{ nodes, links }}
            nodeId={"node_id"}
            nodeRelSize={4}
            nodeVal={(node) => getNodeSize(node)}
            nodeLabel={(node) => {
              if(node.node_type == "DEFAULT_DOMAIN") return ``
              // Only show default label when NOT hovered to prevent duplicate labels
              if(node.hovered) return ``
              return `${node.name}`
            }}
            nodeCanvasObjectMode={() => 'replace'}
            linkCanvasObjectMode={() => "after"}
            linkWidth={0.0001}
            
            // Make canvas background transparent so our CSS grid shows through
            backgroundColor="rgba(0,0,0,0)"

            nodeCanvasObject={(node, ctx, scale) => drawNodes(node, ctx, scale, imgs, canvasOptionRef.current, shouldGreyOutNodes.current, transition.current, highlightedNodeIds)}
            onNodeHover={(node, prevNode) => {
              if (!pinnedNode.current) {
                updateNodeHover(node, prevNode, false)(); // Call the returned function
              }
            }}
            onLinkHover={(link, prevLink) => !pinnedNode.current && updateLinkHover(link, prevLink)}

            linkCanvasObject={(link, ctx, scale) => drawLinks(link, ctx, scale, canvasOptionRef.current, shouldGreyOutNodes.current, highlightedNodeIds)}
            linkPointerAreaPaint={(link, color, ctx) => hoverAreaLink(link, color, ctx)}
            // linkLineDash={(link) => link.hovered ? link.hovered && dashRef.current : [0, 20]}

            onNodeRightClick={(node, event) => updateRenderEvent(event, "node-right", { node })}
            onNodeClick={(node, event) => {
              handleNodePinning(node)
              updateRenderEvent(event, "node-left", { node })
            }}
            onLinkClick={(link, event) => updateRenderEvent(event, "link-left", { link })}
            onLinkRightClick={(link, event) => updateRenderEvent(event, "link-right", { link })}

            onBackgroundClick={(event) => {
              handleNodePinning()
              updateRenderEvent(event, "canvas-left", {})
            }}
            onBackgroundRightClick={(event) => updateRenderEvent(event, "canvas-right", {})}

            onNodeDragEnd={() => updateRenderEvent(undefined, "canvas-left", {})}
            onZoom={() => updateRenderEvent(undefined, "canvas-left", {})}
            linkDirectionalParticles={20}
            linkDirectionalParticleSpeed={0.004}
            linkDirectionalParticleWidth={(link) => link.hovered ? 4 : 0}
          />
        </div>
      )
    })

export default Canvas;
